import { useState, useEffect } from 'react';
import { apiClient } from '../../contexts/AuthContext';
import { Shield, ChevronDown, ChevronUp } from 'lucide-react';

const fmt = (n) => (n ?? 0).toLocaleString();

function RouteLine({ icon, label, reel, cible, unit, sens, ok }) {
  const hasReel = reel != null;
  return (
    <div className={`flex items-center justify-between text-[10px] font-mono rounded px-2 py-1 ${ok ? 'bg-[#2e7d32]/15 text-[#4caf50]' : hasReel ? 'bg-[#0F0F0F] text-zinc-500' : 'bg-[#0F0F0F] text-zinc-400'}`}>
      <span>{icon} {label}</span>
      <span>
        {hasReel ? `${reel}${unit} ` : ''}{hasReel ? (ok ? '✓' : '✗') : '→'} {sens} {cible}{unit}
      </span>
    </div>
  );
}

function routesOf(d) {
  return [
    { icon: '⚖️', label: 'Poids', reel: d.poids_reel, cible: d.poids_cible, unit: 'kg', sens: '≤', ok: d.poids_reel != null && d.poids_reel <= d.poids_cible },
    { icon: '🔥', label: 'Gras', reel: d.mg_reel, cible: d.mg_cible, unit: '%', sens: '≤', ok: d.mg_reel != null && d.mg_cible != null && d.mg_reel <= d.mg_cible },
    { icon: '💪', label: 'Muscle', reel: d.muscle_reel, cible: d.muscle_cible, unit: 'kg', sens: '≥', ok: d.muscle_reel != null && d.muscle_reel >= d.muscle_cible },
  ].filter(r => r.cible != null);
}

function PalierRow({ pal, isNext }) {
  const [open, setOpen] = useState(isNext);
  const d = pal.detail;
  const routes = d ? routesOf(d) : [];
  const winner = routes.find(r => r.ok);
  const today = new Date(Date.now() - 10 * 3600 * 1000).toISOString().slice(0, 10);
  const attentePesee = pal.status === 'gris' && pal.label !== 'P1' && pal.date && pal.date <= today;

  const cfg = {
    vert: { badge: '🟢 SÉCURISÉ', cls: 'text-[#4caf50]', amount: `+${fmt(pal.montant)} XPF` },
    rouge: { badge: '🔴 PERDU', cls: 'text-[#e74c3c]', amount: `-${fmt(pal.montant)} XPF` },
    gris: { badge: pal.label === 'P1' ? '⏳ EN ATTENTE' : attentePesee ? '⏳ ATTENTE PESÉE' : '⚪ À VENIR', cls: attentePesee ? 'text-[#FFB800]' : 'text-zinc-500', amount: `${fmt(pal.montant)} en jeu` },
  }[pal.status];

  let explication = null;
  if (pal.label === 'P1') {
    const j0 = d && d.poids_reel != null
      ? ` · Pesée J0${d.date_mesure ? ` (${d.date_mesure})` : ''} : ${d.poids_reel} kg${d.mg_reel != null ? ` · ${d.mg_reel}% gras` : ''}${d.muscle_reel != null ? ` · ${d.muscle_reel} kg muscle` : ''}`
      : '';
    explication = (pal.status === 'vert' ? 'Caution payée et validée par le coach ✓' : 'Se valide dès le paiement de ta caution au coach') + j0;
  } else if (pal.status === 'vert' && winner) {
    explication = `Sauvé grâce à la route ${winner.label.toUpperCase()} : ${winner.reel}${winner.unit} ${winner.sens} ${winner.cible}${winner.unit} (pesée ${d.date_mesure})`;
  } else if (pal.status === 'rouge') {
    explication = d?.date_mesure
      ? `Perdu : aucune des 3 routes atteinte à la pesée du ${d.date_mesure} → ${fmt(pal.montant)} XPF dans la cagnotte des lots`
      : `Perdu : aucune pesée rentrée dans les 3 jours suivant le palier → ${fmt(pal.montant)} XPF dans la cagnotte des lots`;
  } else if (isNext) {
    explication = attentePesee
      ? `Pesée du palier pas encore publiée par le coach — tes cibles à atteindre :`
      : `Pour sécuriser ${fmt(pal.montant)} XPF${pal.date ? ` avant le ${pal.date}` : ''}, atteins UNE de ces 3 cibles :`;
  } else if (pal.status === 'gris') {
    explication = pal.date ? `Se joue à la pesée du ${pal.date}` : null;
  }

  const expandable = routes.length > 0 && pal.label !== 'P1';

  return (
    <div className={`rounded-lg border ${pal.status === 'vert' ? 'border-[#2e7d32]/40' : pal.status === 'rouge' ? 'border-[#c0392b]/40' : isNext ? 'border-[#FFB800]/40' : 'border-[#2A2A2E]'} bg-[#0F0F0F]`} data-testid={`ma-caution-palier-${pal.label}`}>
      <button onClick={() => expandable && setOpen(o => !o)} className="w-full flex items-center justify-between p-2.5 text-left">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs font-bold text-white w-8">{pal.label}</span>
          {pal.date && <span className="text-[9px] text-zinc-600">{pal.date}</span>}
          {isNext && <span className="text-[8px] font-bold text-[#FFB800] bg-[#FFB800]/15 rounded px-1.5 py-0.5">PROCHAIN</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-bold ${cfg.cls}`}>{cfg.badge}</span>
          <span className={`font-mono text-[10px] ${cfg.cls}`}>{cfg.amount}</span>
          {expandable && (open ? <ChevronUp className="w-3 h-3 text-zinc-600" /> : <ChevronDown className="w-3 h-3 text-zinc-600" />)}
        </div>
      </button>
      {(explication && (open || !expandable)) && (
        <p className={`px-2.5 pb-2 text-[10px] leading-relaxed ${pal.status === 'rouge' ? 'text-[#e74c3c]/90' : pal.status === 'vert' ? 'text-[#4caf50]/90' : 'text-zinc-400'}`}>{explication}</p>
      )}
      {open && routes.length > 0 && (
        <div className="px-2.5 pb-2.5 space-y-1">
          {routes.map(r => <RouteLine key={r.label} {...r} />)}
          <p className="text-[8px] text-zinc-600 text-center pt-0.5">Une seule route suffit ✓</p>
        </div>
      )}
    </div>
  );
}

export function MaCautionCard({ nom }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!nom) return;
    apiClient.get('/challenge/cautions').then(({ data }) => setData(data)).catch(() => {});
  }, [nom]);

  const me = data?.participants?.find(p => p.nom?.toUpperCase() === nom?.toUpperCase());
  if (!me || !(me.paliers || []).length) return null;

  const saved = me.paliers.filter(p => p.status === 'vert').reduce((s, p) => s + p.montant, 0);
  const lost = me.paliers.filter(p => p.status === 'rouge').reduce((s, p) => s + p.montant, 0);
  const pending = me.paliers.filter(p => p.status === 'gris').reduce((s, p) => s + p.montant, 0);
  const nextPal = me.paliers.find(p => p.status === 'gris' && p.label !== 'P1');
  const rp = data.routes_pct || { poids: 4, gras: 4, muscle: 4 };

  return (
    <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-xl overflow-hidden" data-testid="ma-caution-card">
      <div className="p-4 pb-3">
        <h2 className="text-base font-bold text-white flex items-center gap-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
          <Shield className="w-4 h-4 text-[#FFB800]" /> Ma Caution
        </h2>
        <div className="grid grid-cols-3 gap-2 mt-3">
          <div className="bg-[#0F0F0F] rounded-lg p-2 text-center border border-[#2e7d32]/30">
            <p className="text-[8px] text-zinc-500 uppercase font-bold">Sécurisé</p>
            <p className="text-xs font-black text-[#4caf50] font-mono" data-testid="ma-caution-saved">{fmt(saved)}</p>
          </div>
          <div className="bg-[#0F0F0F] rounded-lg p-2 text-center border border-[#c0392b]/30">
            <p className="text-[8px] text-zinc-500 uppercase font-bold">Perdu</p>
            <p className="text-xs font-black text-[#e74c3c] font-mono" data-testid="ma-caution-lost">{fmt(lost)}</p>
          </div>
          <div className="bg-[#0F0F0F] rounded-lg p-2 text-center border border-[#2A2A2E]">
            <p className="text-[8px] text-zinc-500 uppercase font-bold">En jeu</p>
            <p className="text-xs font-black text-zinc-300 font-mono" data-testid="ma-caution-pending">{fmt(pending)}</p>
          </div>
        </div>
        <div className="space-y-1.5 mt-3">
          {me.paliers.map(pal => (
            <PalierRow key={pal.label} pal={pal} isNext={nextPal && pal.label === nextPal.label} />
          ))}
        </div>
        <details className="mt-3 bg-[#0F0F0F] rounded-lg border border-[#2A2A2E]">
          <summary className="p-2.5 text-[10px] font-bold text-[#FFB800] cursor-pointer">📖 Rappel des règles</summary>
          <div className="px-2.5 pb-2.5 text-[10px] text-zinc-400 space-y-1 leading-relaxed">
            <p>Ta caution est jouée en 4 parts de {fmt(me.paliers[0]?.montant)} XPF : <span className="font-mono text-white">P1</span> (paiement) puis <span className="font-mono text-white">S4, S8, S12</span>.</p>
            <p>À chaque palier, il faut atteindre <span className="text-white font-bold">UNE des 3 routes</span> (au prorata : S4 = 33%, S8 = 66%, S12 = 100%) :</p>
            <p className="pl-2">⚖️ Poids <span className="text-white">−{rp.poids}%</span> · 🔥 Gras <span className="text-white">−{rp.gras}%</span> · 💪 Muscle <span className="text-white">+{rp.muscle}%</span> (par rapport à ton J0)</p>
            <p>🟢 Route atteinte = part sécurisée · 🔴 Aucune route (ou pas de pesée) = part perdue → cagnotte des lots 🎁</p>
          </div>
        </details>
      </div>
    </div>
  );
}
